import api from '../api/axios';

export type AttendanceStatus = 'attended' | 'no-show';
export type NoShowReason = 'sick' | 'holiday' | 'family' | 'other';

// Frontend-side shape — uses 'HH:MM' for times bound to UI form fields,
// and a separate `attendanceDate` for the calendar date. The service
// composes/decomposes ISO timestamps for the API.
export interface AttendanceRecord {
  id?: number;            // server id; undefined for unsaved
  serverId?: number;
  scheduleItemServerId: number;
  childId: number;
  attendanceDate: string;   // 'YYYY-MM-DD'
  actualArrival: string | null;   // 'HH:MM' or null (no-show / unknown)
  actualDeparture: string | null; // 'HH:MM' or null
  arrivalComment: string | null;
  departureComment: string | null;
  loggedByUserId?: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isoToDateOnly(v: unknown): string | null {
  if (typeof v !== 'string' || v.length < 10) return null;
  return v.substring(0, 10);
}

// Extract 'HH:MM' from any timestamp-like string. After the API's
// pgTypes.setTypeParser(1114) tweak, TIMESTAMP comes back verbatim — usually
// 'YYYY-MM-DD HH:MM:SS' with a space separator, but historic data and
// JSON-serialised Date objects use 'T'. Match either.
function isoToHHMM(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  // First HH:MM in the string is the hour-minute we want (year is YYYY-MM-DD,
  // doesn't match the HH:MM regex).
  const m = v.match(/(\d{2}):(\d{2})/);
  if (!m) return null;
  return `${m[1]}:${m[2]}`;
}

// 'HH:MM' on a given 'YYYY-MM-DD' → ISO timestamp (no TZ designator) so the
// API stores wall-clock time without timezone shift.
function composeTimestamp(date: string, hhmm: string): string {
  return `${date}T${hhmm}:00`;
}

function rowToRecord(row: any): AttendanceRecord | null {
  const date = isoToDateOnly(row.attendance_date);
  if (!date) return null;
  return {
    id: row.id,
    serverId: row.id,
    scheduleItemServerId: row.schedule_item_id,
    childId: row.child_id,
    attendanceDate: date,
    actualArrival: row.actual_arrival ? isoToHHMM(row.actual_arrival) : null,
    actualDeparture: row.actual_departure ? isoToHHMM(row.actual_departure) : null,
    arrivalComment: row.arrival_comment ?? null,
    departureComment: row.departure_comment ?? null,
    loggedByUserId: row.logged_by_user_id,
  };
}

// ─── Service ─────────────────────────────────────────────────────────────────

export class AttendanceDataService {
  /** List attendance logs for a date range (org-scoped server-side). */
  static async listAttendance(
    from: string,
    to: string,
    filters?: { childId?: number; scheduleItemId?: number },
  ): Promise<AttendanceRecord[]> {
    const params: Record<string, string | number> = { from, to };
    if (filters?.childId != null) params.childId = filters.childId;
    if (filters?.scheduleItemId != null) params.scheduleItemId = filters.scheduleItemId;
    const response = await api.get('/attendance', { params });
    const rows = Array.isArray(response.data) ? response.data : [];
    return rows
      .map(rowToRecord)
      .filter((r: AttendanceRecord | null): r is AttendanceRecord => r !== null);
  }

  /** Create a new attendance log. */
  static async createAttendance(payload: {
    scheduleItemServerId: number;
    childId: number;
    attendanceDate: string;
    actualArrival: string | null;     // 'HH:MM' or null
    actualDeparture: string | null;
    arrivalComment: string | null;
    departureComment: string | null;
  }): Promise<{ id: number }> {
    const body = {
      schedule_item_id: payload.scheduleItemServerId,
      child_id: payload.childId,
      attendance_date: payload.attendanceDate,
      actual_arrival: payload.actualArrival
        ? composeTimestamp(payload.attendanceDate, payload.actualArrival)
        : null,
      actual_departure: payload.actualDeparture
        ? composeTimestamp(payload.attendanceDate, payload.actualDeparture)
        : null,
      arrival_comment: payload.arrivalComment,
      departure_comment: payload.departureComment,
    };
    const response = await api.post('/attendance', body);
    return { id: response.data.id };
  }

  /** Patch an existing attendance log. Sparse: only the supplied fields update. */
  static async updateAttendance(
    serverId: number,
    attendanceDate: string,
    updates: {
      actualArrival?: string | null;
      actualDeparture?: string | null;
      arrivalComment?: string | null;
      departureComment?: string | null;
    },
  ): Promise<void> {
    const body: Record<string, unknown> = {};
    if ('actualArrival' in updates) {
      body.actual_arrival = updates.actualArrival
        ? composeTimestamp(attendanceDate, updates.actualArrival)
        : null;
    }
    if ('actualDeparture' in updates) {
      body.actual_departure = updates.actualDeparture
        ? composeTimestamp(attendanceDate, updates.actualDeparture)
        : null;
    }
    if ('arrivalComment' in updates) body.arrival_comment = updates.arrivalComment;
    if ('departureComment' in updates) body.departure_comment = updates.departureComment;
    if (Object.keys(body).length === 0) return;
    await api.patch(`/attendance/${serverId}`, body);
  }

  /** Remove an attendance log entirely. */
  static async deleteAttendance(serverId: number): Promise<void> {
    await api.delete(`/attendance/${serverId}`);
  }
}
